/**
 * Componente FileUpload - Subida de archivos con drag & drop
 *
 * Permite subir archivos arrastrándolos o seleccionándolos
 * Muestra una lista de archivos seleccionados
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  Paper,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { COLORS } from '../theme/theme';

/**
 * Componente de subida de archivos
 *
 * @param {Object} props
 * @param {Function} props.onFilesChange - Callback cuando cambian los archivos seleccionados
 * @param {string} props.acceptedTypes - Tipos de archivo aceptados (default: '.pdf')
 * @param {boolean} props.multiple - Permitir múltiples archivos (default: true)
 * @param {string} props.label - Texto del label
 */
const FileUpload = ({
  onFilesChange,
  acceptedTypes = '.pdf',
  multiple = true,
  label = 'Arrastra archivos PDF aquí o haz clic para seleccionar',
}) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');

  // Validar tipo de archivo
  const validateFile = (file) => {
    const acceptedExtensions = acceptedTypes.split(',').map((ext) => ext.trim());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    return acceptedExtensions.includes(fileExtension);
  };

  // Manejar archivos seleccionados
  const handleFiles = (newFiles) => {
    setError('');
    const validFiles = [];
    const invalidFiles = [];

    Array.from(newFiles).forEach((file) => {
      if (validateFile(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setError(`Archivos no válidos: ${invalidFiles.join(', ')}`);
    }

    const updatedFiles = multiple ? [...files, ...validFiles] : validFiles;
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  // Manejar selección de archivos
  const handleFileInput = (event) => {
    if (event.target.files.length > 0) {
      handleFiles(event.target.files);
    }
  };

  // Manejar drag & drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // Eliminar archivo
  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Área de drop */}
      <Paper
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: `2px dashed ${isDragging ? COLORS.primaryLight : COLORS.primary}`,
          backgroundColor: isDragging ? COLORS.backgroundLight : COLORS.backgroundPaper,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: COLORS.tealLight,
            backgroundColor: '#eef5f6',
          },
        }}
        onClick={() => document.getElementById('file-input').click()}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: COLORS.primary, mb: 2 }} />
        <Typography variant="body1" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Archivos aceptados: {acceptedTypes}
        </Typography>

        {/* Input oculto */}
        <input
          id="file-input"
          type="file"
          multiple={multiple}
          accept={acceptedTypes}
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </Paper>

      {/* Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Lista de archivos seleccionados */}
      {files.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Archivos seleccionados ({files.length})
          </Typography>
          <List>
            {files.map((file, index) => (
              <ListItem
                key={index}
                sx={{
                  backgroundColor: COLORS.backgroundLight,
                  borderRadius: 1,
                  mb: 1,
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveFile(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <PdfIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={formatFileSize(file.size)}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
